const { DEBUG_PUSH_NOTIFICATIONS } = require('utils/constants');
const Firebase = require('utils/firebasex');
const Storage = require('utils/storage');
const CreateCommunicationService = require('services/communication');
const SupportDiagnostics = require('services/support-diagnostics');
const AppDiagnostics = require('./app-diagnostics');
const PersistenceConfig = require('utils/persistence-config');

const internals = {
    lifecycleVersion: 0,
    pausePromise: Promise.resolve(),
    resume: null
};

exports.currentFirebaseTokenKey = PersistenceConfig.keys.cordova.currentFirebaseToken;

exports.backgroundFirebaseTokenKey = PersistenceConfig.keys.cordova.backgroundFirebaseToken;

exports.PUSH_NOTIFICATION_CHECK_DAYS_INTERVAL = 5;

exports.isCordovaRunning = () => !!window.cordova;

exports.persistSet = (key, value) => Storage.setItem(key, value);

exports.persistGet = (key) => Storage.getItem(key);

exports.persistRemove = (key) => Storage.removeItem(key);

exports.persistValIsSet = (val) => typeof val !== 'undefined' && val !== null && val !== 'null';

exports.isAndroid = () => window.device.platform?.toLowerCase() === 'android';

exports.isIOS = () => window.device.platform?.toLowerCase() === 'ios';

exports.updateFirebaseToken = (token) => Firebase.saveTokenIfChanged(token);

exports.onDeviceReady = async () => {

    AppDiagnostics.record('push.device-ready');
    console.log('CORDOVA DEVICE READY');
    SupportDiagnostics.recordCordovaEvent('device-ready');
    const { context } = require('app-context');
    const { analyticsTemplates: analytics } = require('utils/analytics');

    const api = context.api.nearpeer;
    const handlers = context.handlers;
    const redux = context.redux.hooks;
    const actions = context.actions;

    const token = exports.persistGet(exports.currentFirebaseTokenKey);
    const previousTokenExists = exports.persistValIsSet(token);

    if (window.StatusBar) {
        StatusBar.overlaysWebView(true);
        StatusBar.backgroundColorByHexString('#000000');
        StatusBar.styleLightContent(); // white text
    }

    Promise.resolve(Firebase.init({
        analytics,
        askPermissionNow: true,
        onPush: ({ data, tap, raw }) => exports.onPushReceive(data, { tap, finish: raw?.finish || data?.finish }),
        saveToken: async (newToken) => {

            if (DEBUG_PUSH_NOTIFICATIONS) {
                console.log('updateFirebaseToken: ' + newToken);
            }

            const bindingType = exports.getBindingType();

            await api.authentication.storeFirebaseToken({ bindingType, token: newToken });

            redux.dispatch(actions.app.setFirebaseToken({ token: newToken }));

            exports.persistRemove(exports.backgroundFirebaseTokenKey);
            exports.persistSet(exports.currentFirebaseTokenKey, newToken);
        }
    })).catch((error) => {

        AppDiagnostics.record('push.initialization-failed', { name: error?.name, code: error?.code });
        console.warn('[Push] initialization failed', error);
    });

    // If we have a push endpoint from a past session, get it into app state
    if (previousTokenExists) {
        redux.dispatch(actions.app.setFirebaseToken({ token }));
    }

    // Wait for auth to settle
    AppDiagnostics.record('push.auth.wait');
    await handlers.auth.resolveAuthStatus();

    // Run queued up stuff
    Firebase.markRouterReady();
    AppDiagnostics.record('push.router-ready');
};

exports.getBindingType = () => {

    return exports.isAndroid() ? 'fcm' : 'apn';
};

// The app runs this when the user either backgrounds OR closes the app.
exports.onPause = () => {

    AppDiagnostics.record('lifecycle.pause');
    console.log('CORDOVA PAUSE');
    SupportDiagnostics.recordCordovaEvent('pause');

    const { context } = require('app-context');

    const communication = CreateCommunicationService.fromContext(context);
    const handlers = context.handlers;
    const redux = context.redux.hooks;
    const actions = context.actions;

    redux.dispatch(actions.app.setAppInBackground({ inBackground: true }));

    internals.lifecycleVersion += 1;
    internals.pausePromise = internals.pausePromise.then(async () => {

        AppDiagnostics.record('pause.stop-listeners.wait');
        await handlers.communication.stopGlobalListening();
        AppDiagnostics.record('pause.shutdown.wait');
        await communication.shutdown();
        AppDiagnostics.record('pause.shutdown.ready');
    }).catch((error) => {

        AppDiagnostics.record('pause.failed', { name: error?.name, code: error?.code });
        console.error('[cordova] communication shutdown failed', error);
    });

    return internals.pausePromise;
};

exports.getIsAuthenticated = () => {

    const { context } = require('app-context');

    const selectors = context.selectors.all;
    const redux = context.redux.hooks;

    return selectors.getIsAuthenticated(redux.getState());
};

exports.onResume = async () => {

    AppDiagnostics.record('lifecycle.resume');
    console.log('CORDOVA ON_RESUME');
    SupportDiagnostics.recordCordovaEvent('resume');
    const { context } = require('app-context');

    const communication = CreateCommunicationService.fromContext(context);
    const handlers = context.handlers;
    const selectors = context.selectors.all;
    const redux = context.redux.hooks;
    const actions = context.actions;
    const version = internals.lifecycleVersion;

    if (internals.resume?.version === version) {
        AppDiagnostics.record('resume.coalesced');
        return internals.resume.promise;
    }

    // Avoids running this twice.
    const isInBackground = selectors.getIsAppInBackground(redux.getState());

    if (isInBackground)  {
        redux.dispatch(actions.app.setAppInBackground({ inBackground: false }));
    }

    if (!exports.getIsAuthenticated() || !isInBackground) {
        AppDiagnostics.record('resume.skipped', { authenticated: exports.getIsAuthenticated(), wasInBackground: isInBackground });
        return;
    }

    const sessionToken = selectors.getApiToken(redux.getState());
    const isCurrentResume = () => version === internals.lifecycleVersion
        && exports.getIsAuthenticated()
        && selectors.getApiToken(redux.getState()) === sessionToken;
    const promise = (async () => {

        AppDiagnostics.record('resume.pause-completion.wait');
        await internals.pausePromise;
        AppDiagnostics.record('resume.pause-completion.ready');

        if (!isCurrentResume()) {
            return;
        }

        AppDiagnostics.record('resume.transport.wait');
        await communication.initialize();
        AppDiagnostics.record('resume.transport.ready');

        if (!isCurrentResume()) {
            return;
        }

        // Refresh metadata without keeping the destination behind a loader.
        Promise.all([
            redux.dispatch(actions.dataFetching.fetchCurrentUser({ skipProcessingWhileMainAppLoading: true })),
            redux.dispatch(actions.dataFetching.fetchAppEntities({
                entities: ['schools', 'categories', 'interests', 'roleGroups', 'classes'],
                schoolId: selectors.getCurrentSchoolId(redux.getState())
            }))
        ]).catch((error) => console.warn('[cordova] background refresh failed', error));

        Promise.resolve(handlers.communication.startGlobalListening()).catch((error) => {

            console.warn('[cordova] communication listeners could not start', error);
        });

        const channelSid = handlers.communication.getCurrentChannelSid?.();

        if (channelSid) {
            Promise.resolve(handlers.communication.startListening(channelSid)).catch((error) => {

                console.warn('[cordova] active chat refresh failed', error);
            });
        }
    })().catch((error) => {

        AppDiagnostics.record('resume.failed', { name: error?.name, code: error?.code });
        console.error('[cordova] communication resume failed', error);
    }).finally(() => {

        if (internals.resume?.promise === promise) {
            internals.resume = null;
        }
    });

    internals.resume = { version, promise };
    return promise;
};

exports.isResumeException = (pathname) => {

    if (exports.isAndroid()) {
        // We added ability to change your profile pic when viewing your own profile.
        // This regex is to cover, e.g. /app/profile/123456
        const userProfile = /^\/app\/profile\/\d+$/;
        if (pathname === '/app/profile/edit' || userProfile.test(pathname)) {
            return true;
        }
    }

    return false;
};

exports.onPushReceive = async (data = {}, { tap = data.tap, finish = data.finish } = {}) => {

    const isTap = tap === 'background' || tap === 'foreground';
    const version = isTap ? internals.pushVersion = (internals.pushVersion || 0) + 1 : undefined;
    const metadata = {
        pushAttemptId: version,
        type: data.type,
        tap,
        schoolId: Number.isSafeInteger(Number(data.schoolId)) ? Number(data.schoolId) : undefined,
        hasLink: !!data.link,
        payloadDestination: data.link ? AppDiagnostics.safeLocation(data.link) : undefined,
        hasChannelSid: !!data.channelSid
    };
    AppDiagnostics.record('push.received', metadata);
    SupportDiagnostics.recordPushNotification(data, { source: 'cordova.onPushReceive', tap });

    try {
        if (!isTap) {
            AppDiagnostics.record('push.ignored', { reason: 'not-a-tap' });
            return;
        }

        const target = internals.getPushDestination(data);

        if (!target) {
            AppDiagnostics.record('push.ignored', { reason: 'missing-or-invalid-destination', type: data.type, pushAttemptId: version });
            return;
        }

        const { context } = require('app-context');
        const requestedSchoolId = Number(new URL(target, 'https://nearpeer.net').searchParams.get('s'));
        AppDiagnostics.record('push.destination', {
            destination: AppDiagnostics.safeLocation(target),
            requestedSchoolId: Number.isSafeInteger(requestedSchoolId) && requestedSchoolId > 0 ? requestedSchoolId : undefined,
            destinationSource: data.link ? 'payload-link' : 'notification-type-fallback',
            ...metadata
        });
        await require('services/auth-management/open-app-destination').openAppDestination({
            path: target,
            store: context.redux.hooks,
            context,
            record: (event, details) => AppDiagnostics.record(event, { ...details, pushAttemptId: version }),
            isCurrent: () => version === internals.pushVersion
        });
        AppDiagnostics.record('push.settled', { current: version === internals.pushVersion, pushAttemptId: version });

        require('utils/analytics').analyticsTemplates.pushNotifs({
            action: 'received_notification',
            label: 'tapped_notif',
            value: `type: ${data.type}, redirectedTo: ${target}, schoolId: ${data.schoolId}`
        });
    }
    catch (error) {
        AppDiagnostics.record('push.failed', { name: error?.name, code: error?.code, pushAttemptId: version });
        throw error;
    }
    finally {
        if (typeof finish === 'function') {
            finish();
        }
    }
};

internals.getPushDestination = (data) => {

    const channel = data.channelSid ? `/${encodeURIComponent(data.channelSid)}` : '';
    let path = data.link;
    if (!path) {
        switch (data.type) {
            case 'dm': path = `/app/messaging${channel}`; break;
            case 'mention':
            case 'group-msg': path = channel ? `/app/classes/chat${channel}` : '/app/classes'; break;
            case 'survey': path = '/app/surveys/my-surveys'; break;
            case 'offer': path = data.offerId ? `/app/offers/${encodeURIComponent(data.offerId)}` : '/app/offers'; break;
            case 'resource': path = data.resourceId ? `/app/resources/${encodeURIComponent(data.resourceId)}` : '/app/resources'; break;
            case 'peer': path = data.peerId ? `/app/profile/${encodeURIComponent(data.peerId)}` : '/app/messaging'; break;
            case 'announcement': path = '/app/welcome'; break;
            case 'school-invite': path = '/app/manage-communities'; break;
            default: return null;
        }
    }

    if (typeof path !== 'string') {
        return null;
    }

    try {
        const url = new URL(path, 'https://nearpeer.net');
        if (url.protocol !== 'https:' || url.username || url.password || url.port
            || !['nearpeer.net', 'staging.nearpeer.net', 'teams.nearpeer.net'].includes(url.hostname)
            || !url.pathname.startsWith('/app/') || path.startsWith('//')) {
            return null;
        }

        const schoolId = Number(data.schoolId);
        if (data.type !== 'school-invite' && Number.isSafeInteger(schoolId) && schoolId > 0) {
            url.searchParams.set('s', String(schoolId));
        }

        return `${url.pathname}${url.search}`;
    }
    catch (_) {
        return null;
    }
};
